/**
 * Translation hook for internationalization
 * 
 * Provides a simple translation function that retrieves translated strings
 * from the language data file. Falls back to English if translation is missing.
 * 
 * @example
 * ```tsx
 * const { t } = useTranslation('en-us')
 * 
 * <h1>{t('welcome_message')}</h1>
 * <button>{t('submit')}</button>
 * ```
 */
import langData from '../data/lang.json'

type TranslationKey = keyof (typeof langData)['en-us']

/**
 * Custom hook for translations
 * 
 * @param locale - The locale string (e.g., 'en-us', 'de-de', 'es-es', 'fr-fr')
 * @returns An object with a translation function `t`
 * 
 * @example
 * ```ts
 * const { t } = useTranslation('de-de')
 * const welcomeText = t('welcome_message') // Returns German translation
 * 
 * const { t } = useTranslation('invalid-locale')
 * const text = t('submit') // Falls back to English
 * ```
 */
export function useTranslation(locale: string) {
  /**
   * Translation function
   * 
   * @param key - The translation key to look up
   * @returns The translated string, or English fallback, or the key itself if not found
   */
  const t = (key: TranslationKey): string => {
    if (locale in langData && key in langData[locale as keyof typeof langData]) {
      return langData[locale as keyof typeof langData][key]
    }
    // Fallback to English if translation is missing
    return langData['en-us'][key] || key
  }

  return { t }
}
